import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUtensils, FaUsers, FaLeaf, FaSmile, FaCity, FaVolumeUp, FaHeart, FaStar, FaStarHalfAlt, FaInstagram, FaTwitter, FaFacebook, FaYoutube, FaBookmark, FaCheck, FaPalette, FaUserCheck } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { playGanjiAdabiAudio, playSmartAudio } from '../utils/audioUtils';
import { env } from '../config/environment';
import { getWordOfTheDay } from '../data/wordOfTheDayData';

interface WordOfTheDay {
  farsi: string;
  english: string;
  transliteration: string;
  example: string;
  exampleTransliteration: string;
  exampleEnglish: string;
  audioUrl?: string;
}

const VocabularyGarden: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wordOfTheDay, setWordOfTheDay] = useState<WordOfTheDay>({
    farsi: 'کتاب',
    english: 'Book',
    transliteration: 'Ketāb',
    example: 'من یک کتاب می‌خوانم',
    exampleTransliteration: 'Man yek ketāb mikhanam',
    exampleEnglish: '(I am reading a book)'
  });
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingWord, setLoadingWord] = useState(true);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  useEffect(() => {
    // Simple script for flower hover effects
    const flowers = document.querySelectorAll('.bloom');
    
    flowers.forEach((flower) => {
      flower.addEventListener('mouseenter', function() {
        (this as HTMLElement).style.transform = 'scale(1.1)';
        (this as HTMLElement).style.filter = 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.5))';
      });
      
      flower.addEventListener('mouseleave', function() {
        (this as HTMLElement).style.transform = '';
        (this as HTMLElement).style.filter = '';
      });
    });
  }, []);

  // Fetch word of the day
  useEffect(() => {
    const fetchWordOfTheDay = async () => {
      try {
        setLoadingWord(true);
        
        // Use local word-of-the-day data instead of API
        const wordData = getWordOfTheDay();
        
        setWordOfTheDay({
          farsi: wordData.persian,
          english: wordData.english,
          transliteration: wordData.transliteration,
          example: wordData.example || '',
          exampleTransliteration: '',
          exampleEnglish: '',
          audioUrl: wordData.audioUrl
        });
      } catch (error) {
        console.error('Error fetching word of the day:', error);
        // Keep the default fallback word if there's an error
      } finally {
        setLoadingWord(false);
      }
    };

    fetchWordOfTheDay();
  }, []);

  const handlePlayAudio = async () => {
    if (!wordOfTheDay.farsi) return;
    
    try {
      setIsPlayingAudio(true);
      setAudioError(null);
      
      // Try to play ganji-adabi audio if available
      if (wordOfTheDay.audioUrl) {
        try {
          // Check if the audio file is a real audio file by trying to fetch it
          const response = await fetch(wordOfTheDay.audioUrl);
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            const contentLength = response.headers.get('content-length');
            
            // If it's a small file or not audio type, it's likely a placeholder
            const isLikelyPlaceholder = !contentType?.includes('audio') || 
                                       (contentLength && parseInt(contentLength) < 1024);
            
            if (isLikelyPlaceholder) {
              console.log('Audio file appears to be a placeholder - no audio available');
              console.log('🚫 Use generate_audio.sh to create audio files');
              return;
            } else {
              // Try to play the actual audio file
              await playGanjiAdabiAudio(wordOfTheDay.audioUrl);
            }
          } else {
            throw new Error('Audio file not found');
          }
        } catch (error) {
          console.warn('Ganji-adabi audio failed:', error);
          console.log('🚫 Use generate_audio.sh to create audio files');
          return;
        }
      } else {
        // No audio URL available
        console.log('🚫 No audio URL available - use generate_audio.sh to create audio files');
        return;
      }
    } catch (error) {
      console.error('Audio playback failed:', error);
      setAudioError('Audio playback failed. Please try again.');
    } finally {
      setIsPlayingAudio(false);
    }
  };

  const handleSaveToGarden = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setSaving(true);
      
      const response = await fetch(`${env.apiUrl}/api/garden/words`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          wordId: `word-${Date.now()}`, // Generate a unique ID
          farsi: wordOfTheDay.farsi,
          english: wordOfTheDay.english,
          transliteration: wordOfTheDay.transliteration,
          category: 'daily',
          difficulty: 'beginner'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.message === 'Word already exists in your garden') {
          setIsSaved(true);
          return;
        }
        throw new Error(errorData.message || 'Failed to save word');
      }

      const data = await response.json();
      
      if (data.success) {
        setIsSaved(true);
        // Show success message
        setTimeout(() => {
          setIsSaved(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving word to garden:', error);
      // You could show a toast notification here
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="font-poppins bg-gradient-to-br from-white via-blue-50 to-indigo-50 text-gray-800" dir="ltr">
      {/* Language Toggle */}
      <div className="absolute top-4 right-4 z-50">
        <div className="flex space-x-2">
          <button className="px-3 py-1 rounded-full bg-white bg-opacity-80 text-indigo-700 font-medium text-sm shadow-sm backdrop-blur-sm">EN</button>
          <button className="px-3 py-1 rounded-full bg-gray-200 text-gray-600 font-medium text-sm">FA</button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-xl">V</div>
          <span className="font-playfair text-2xl font-bold text-indigo-800">Pirooz</span>
        </div>
        <div className="hidden md:flex space-x-6">
          <Link to="/" className="text-indigo-700 hover:text-indigo-900 font-medium">Home</Link>
          <Link to="/features" className="text-gray-600 hover:text-indigo-700 font-medium">Features</Link>
          <Link to="/about" className="text-gray-600 hover:text-indigo-700 font-medium">About</Link>
          <Link to="/contact" className="text-gray-600 hover:text-indigo-700 font-medium">Contact</Link>
        </div>
        <div className="flex items-center space-x-4">
          {user && (
            <Link 
              to="/my-garden" 
              className="flex items-center px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaBookmark className="mr-2" />
              My Garden
            </Link>
          )}
          <button className="md:hidden text-indigo-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-12 md:py-20 relative">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-12 md:mb-0">
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-indigo-800 leading-tight mb-6">
              Grow Your Farsi,<br />One Word at a Time
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-lg">
              Discover the beauty of Persian language through our magical garden where each word blossoms into knowledge. Perfect for beginners and intermediate learners alike.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/alphabet-learning" className="inline-block px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full font-medium shadow-lg hover:shadow-indigo-200 transition-all hover:scale-105">
                Start Learning
              </Link>
              <Link to="/vocabulary" className="inline-block px-8 py-3 border-2 border-indigo-400 text-indigo-600 rounded-full font-medium hover:bg-indigo-50 transition-all">
                Explore Gardens
              </Link>
            </div>
          </div>
          <div className="md:w-1/2 relative">
            {/* Persian Garden Illustration */}
                          <div className="relative w-full max-w-lg mx-auto">
                <div className="absolute -top-10 -left-10 w-24 h-24 bg-indigo-50 rounded-full opacity-70"></div>
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-blue-50 rounded-full opacity-70"></div>
                
                <div className="relative z-10 bg-white p-6 rounded-3xl shadow-xl border border-indigo-100">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4">
                  {/* Garden scene */}
                  <div className="relative h-64 md:h-80">
                    {/* Ground */}
                    <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-indigo-50 to-blue-50 rounded-b-lg"></div>
                    
                    {/* Penguin character */}
                    <div className="absolute bottom-16 left-1/4 w-16 bloom floating">
                      <div className="relative">
                        <div className="w-16 h-16 bg-black rounded-full"></div>
                        <div className="absolute top-1 left-1 w-14 h-14 bg-white rounded-full"></div>
                        <div className="absolute top-4 left-4 w-8 h-8 bg-black rounded-full"></div>
                        <div className="absolute top-5 left-5 w-6 h-6 bg-white rounded-full"></div>
                        <div className="absolute top-10 left-6 w-4 h-4 bg-indigo-400 rounded-full"></div>
                        <div className="absolute -bottom-2 left-4 w-8 h-4 bg-black rounded-b-full"></div>
                        <div className="absolute -bottom-2 right-4 w-8 h-4 bg-black rounded-b-full"></div>
                      </div>
                    </div>
                    
                    {/* Child character */}
                    <div className="absolute bottom-16 right-1/4 w-14 bloom">
                      <div className="relative">
                        <div className="w-12 h-12 mx-auto bg-indigo-200 rounded-full"></div>
                        <div className="w-14 h-16 mx-auto bg-purple-400 rounded-lg mt-1"></div>
                        <div className="absolute -top-2 right-0 w-6 h-8 bg-indigo-500 rounded-full transform rotate-45"></div>
                      </div>
                    </div>
                    
                    {/* Flowers (vocabulary words) */}
                    <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex space-x-4">
                      <div className="bloom relative group">
                        <div className="w-8 h-8 bg-indigo-300 rounded-full"></div>
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-white px-2 py-1 rounded text-xs font-bold text-indigo-800 opacity-0 group-hover:opacity-100 transition-opacity">
                          دوست<br />(Friend)
                        </div>
                      </div>
                      <div className="bloom relative group">
                        <div className="w-6 h-8 bg-purple-400 rounded-full"></div>
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-white px-2 py-1 rounded text-xs font-bold text-indigo-800 opacity-0 group-hover:opacity-100 transition-opacity">
                          گل<br />(Flower)
                        </div>
                      </div>
                      <div className="bloom relative group">
                        <div className="w-7 h-7 bg-lavender-300 rounded-full"></div>
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-white px-2 py-1 rounded text-xs font-bold text-indigo-800 opacity-0 group-hover:opacity-100 transition-opacity">
                          خوشحال<br />(Happy)
                        </div>
                      </div>
                    </div>
                    
                    {/* Watering can */}
                    <div className="absolute bottom-24 right-1/4 w-8 h-10 bg-indigo-300 rounded-lg transform rotate-12 bloom">
                      <div className="absolute top-0 left-0 w-2 h-4 bg-indigo-500 rounded-l-lg"></div>
                      <div className="absolute top-2 left-2 w-4 h-1 bg-indigo-600 rounded-full"></div>
                    </div>
                    
                    {/* Water drops */}
                    <div className="absolute bottom-20 right-1/4 -mr-4 flex flex-col space-y-1">
                      <div className="w-2 h-2 bg-blue-200 rounded-full opacity-70"></div>
                      <div className="w-1.5 h-1.5 bg-blue-200 rounded-full opacity-70 ml-1"></div>
                      <div className="w-1 h-1 bg-blue-200 rounded-full opacity-70"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="container mx-auto px-6 py-12 bg-gradient-to-b from-lavender-50 to-white rounded-t-3xl -mt-12">
        <div className="text-center mb-12">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-indigo-800 mb-4">Explore Vocabulary Gardens</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">Each garden represents a different category of words to help you grow your Farsi knowledge.</p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Category Cards */}
          <Link to="/vocabulary/kitchen" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center">
              <FaUtensils className="text-4xl text-indigo-600" />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Kitchen & Cooking</h3>
              <p className="text-gray-600 text-sm">Essential cooking vocabulary</p>
            </div>
          </Link>
          
          <Link to="/vocabulary/family" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
              <FaUsers className="text-4xl text-purple-600" />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Family & Relationships</h3>
              <p className="text-gray-600 text-sm">Family terms and relationships</p>
            </div>
          </Link>
          
          <Link to="/vocabulary/nature" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <FaLeaf className="text-4xl text-blue-600" />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Nature & Environment</h3>
              <p className="text-gray-600 text-sm">Nature and environmental terms</p>
            </div>
          </Link>
          
          <Link to="/vocabulary/feelings" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-lavender-100 to-lavender-200 flex items-center justify-center">
              <FaSmile className="text-4xl text-lavender-600" />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Emotions & Feelings</h3>
              <p className="text-gray-600 text-sm">Express emotions in Persian</p>
            </div>
          </Link>

          <Link to="/vocabulary/colors" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-pink-100 to-orange-200 flex items-center justify-center">
              <FaPalette className="text-4xl text-pink-600" />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Colors & Shades</h3>
              <p className="text-gray-600 text-sm">Beautiful Persian color vocabulary</p>
            </div>
          </Link>
          
          <Link to="/vocabulary/city" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
              <FaCity className="text-4xl text-slate-600" />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">City & Urban Life</h3>
              <p className="text-gray-600 text-sm">Urban vocabulary and city life</p>
            </div>
          </Link>

          <Link to="/vocabulary/food" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-violet-100 to-violet-200 flex items-center justify-center">
              <span className="text-4xl">🍽️</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Food & Dining</h3>
              <p className="text-gray-600 text-sm">Persian food culture and dining</p>
            </div>
          </Link>

          <Link to="/vocabulary/travel" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center">
              <span className="text-4xl">✈️</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Travel & Transportation</h3>
              <p className="text-gray-600 text-sm">Travel and transportation terms</p>
            </div>
          </Link>

          <Link to="/vocabulary/business" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center">
              <span className="text-4xl">💼</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Business & Work</h3>
              <p className="text-gray-600 text-sm">Professional and business vocabulary</p>
            </div>
          </Link>

          <Link to="/vocabulary/clothing" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-rose-100 to-pink-200 flex items-center justify-center">
              <span className="text-4xl">👕</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Clothing & Fashion</h3>
              <p className="text-gray-600 text-sm">Persian vocabulary for clothing and accessories</p>
            </div>
          </Link>

          <Link to="/vocabulary/body-parts" className="bg-white rounded-xl overflow-hidden shadow-md card-hover hover:shadow-xl transition-all duration-300">
            <div className="h-32 bg-gradient-to-br from-red-100 to-pink-200 flex items-center justify-center">
              <FaUserCheck className="text-4xl text-red-600" />
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-indigo-800 mb-2">Body Parts</h3>
              <p className="text-gray-600 text-sm">Human anatomy and body vocabulary</p>
            </div>
          </Link>
        </div>
        
        <div className="text-center mt-12">
          <Link 
            to="/vocabulary" 
            className="inline-block px-6 py-2 border-2 border-indigo-400 text-indigo-600 rounded-full font-medium hover:bg-indigo-50 transition-all"
          >
            View All Categories
          </Link>
        </div>
      </section>

      {/* Word of the Day */}
      <section className="container mx-auto px-6 py-16">
        <div className="bg-gradient-to-br from-indigo-100 to-lavender-50 rounded-3xl p-8 md:p-12 shadow-lg">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/3 mb-8 md:mb-0 flex justify-center">
              <div className="w-48 h-48 bg-white rounded-full flex items-center justify-center shadow-inner border-8 border-indigo-200">
                <div className="text-center">
                  {loadingWord ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin h-12 w-12 text-indigo-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  ) : (
                    <>
                      <div className="text-5xl font-bold text-indigo-700 mb-2">{wordOfTheDay.farsi}</div>
                      <div className="text-gray-600 font-medium">({wordOfTheDay.english})</div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="md:w-2/3 md:pl-12">
              <div className="mb-2 text-indigo-600 font-medium">Word of the Day</div>
              {loadingWord ? (
                <div className="animate-pulse">
                  <div className="h-8 bg-indigo-200 rounded mb-4"></div>
                </div>
              ) : (
                <h2 className="font-playfair text-3xl font-bold text-indigo-800 mb-4">{wordOfTheDay.farsi} / {wordOfTheDay.transliteration}</h2>
              )}
              
              <div className="mb-6">
                {loadingWord ? (
                  <div className="animate-pulse">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center mr-3"></div>
                      <div className="h-4 bg-indigo-200 rounded w-48"></div>
                    </div>
                    <div className="ml-11 h-10 bg-indigo-200 rounded-lg w-48"></div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-indigo-200 flex items-center justify-center mr-3">
                        <FaVolumeUp className="text-indigo-700" />
                      </div>
                      <span className="text-gray-700">Pronunciation: {wordOfTheDay.transliteration}</span>
                    </div>
                    <button
                      onClick={handlePlayAudio}
                      disabled={isPlayingAudio}
                      className="ml-11 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg font-medium hover:bg-indigo-200 transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPlayingAudio ? (
                        <svg className="animate-spin mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <FaVolumeUp className="mr-2" />
                      )}
                      {isPlayingAudio ? 'Playing...' : 'Listen to Pronunciation'}
                    </button>
                    {audioError && (
                      <div className="ml-11 mt-2 text-red-600 text-sm">
                        {audioError}
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {loadingWord ? (
                <div className="animate-pulse mb-6">
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <div className="h-4 bg-indigo-200 rounded w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-64 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-48"></div>
                  </div>
                </div>
              ) : (
                wordOfTheDay.example && (
                  <div className="bg-white rounded-xl p-4 mb-6 shadow-sm">
                    <h4 className="font-bold text-indigo-700 mb-2">Example Sentence:</h4>
                    <p className="text-gray-700 mb-2">{wordOfTheDay.example}</p>
                    {wordOfTheDay.exampleTransliteration && (
                      <p className="text-gray-600">{wordOfTheDay.exampleTransliteration}</p>
                    )}
                    {wordOfTheDay.exampleEnglish && (
                      <p className="text-gray-600 italic">{wordOfTheDay.exampleEnglish}</p>
                    )}
                  </div>
                )
              )}
              
              {loadingWord ? (
                <div className="animate-pulse">
                  <div className="h-10 bg-indigo-200 rounded-full w-40"></div>
                </div>
              ) : (
                <button 
                  onClick={handleSaveToGarden} 
                  className="px-6 py-2 bg-indigo-500 text-white rounded-full font-medium hover:bg-indigo-600 transition-all flex items-center"
                  disabled={saving}
                >
                  {saving ? (
                    <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <FaHeart className="mr-2" />
                  )}
                  {saving ? 'Saving...' : 'Save to My Garden'}
                </button>
              )}
              {isSaved && (
                <div className="mt-4 text-green-600 text-center">
                  <FaCheck className="mr-2" /> Saved to your garden!
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gradient-to-r from-indigo-800 to-purple-800 text-white py-16">
        <div className="container mx-auto px-6">
          <h2 className="font-playfair text-3xl text-center font-bold mb-12">What Our Learners Say</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white bg-opacity-10 p-6 rounded-xl backdrop-filter backdrop-blur-sm">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-indigo-600 mr-4"></div>
                <div>
                  <h4 className="font-bold">Sarah M.</h4>
                  <div className="flex text-indigo-300">
                    <FaStar />
                    <FaStar />
                    <FaStar />
                    <FaStar />
                    <FaStar />
                  </div>
                </div>
              </div>
              <p className="text-indigo-100">"Learning Farsi with Pirooz feels like playing in a magical garden. The words stick because of the beautiful associations!"</p>
            </div>
            
            <div className="bg-white bg-opacity-10 p-6 rounded-xl backdrop-filter backdrop-blur-sm">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-indigo-600 mr-4"></div>
                <div>
                  <h4 className="font-bold">Amir K.</h4>
                  <div className="flex text-indigo-300">
                    <FaStar />
                    <FaStar />
                    <FaStar />
                    <FaStar />
                    <FaStar />
                  </div>
                </div>
              </div>
              <p className="text-indigo-100">"As a heritage learner, Pirooz has helped me connect with my roots in the most delightful way. The visual approach is brilliant."</p>
            </div>
            
            <div className="bg-white bg-opacity-10 p-6 rounded-xl backdrop-filter backdrop-blur-sm">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full bg-indigo-600 mr-4"></div>
                <div>
                  <h4 className="font-bold">Layla J.</h4>
                  <div className="flex text-indigo-300">
                    <FaStar />
                    <FaStar />
                    <FaStar />
                    <FaStar />
                    <FaStarHalfAlt />
                  </div>
                </div>
              </div>
              <p className="text-indigo-100">"I've tried many apps but Pirooz's garden metaphor makes vocabulary building feel organic and natural. Highly recommend!"</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-indigo-800 mb-6">Ready to Cultivate Your Farsi Skills?</h2>
          <p className="text-gray-600 mb-8">Join thousands of learners who have discovered the joy of Persian through our magical vocabulary gardens.</p>
          <Link to="/register" className="inline-block px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full font-medium shadow-lg hover:shadow-indigo-200 transition-all hover:scale-105">
            Start Your Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-indigo-900 to-purple-900 text-indigo-100 pt-12 pb-6">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm">V</div>
                <span className="font-playfair text-xl font-bold text-white">Pirooz</span>
              </div>
              <p className="text-indigo-200">Growing Farsi vocabulary through magical gardens since 2023.</p>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Explore</h4>
              <ul className="space-y-2">
                <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/vocabulary-garden" className="hover:text-white transition-colors">Gardens</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/carreers" className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Connect</h4>
              <div className="flex space-x-4 mb-4">
                <a href="https://instagram.com/Piroozapp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-indigo-800 hover:bg-indigo-700 flex items-center justify-center text-white transition-colors">
                  <FaInstagram />
                </a>
                <a href="https://twitter.com/Piroozapp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-indigo-800 hover:bg-indigo-700 flex items-center justify-center text-white transition-colors">
                  <FaTwitter />
                </a>
                <a href="https://facebook.com/Piroozapp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-indigo-800 hover:bg-indigo-700 flex items-center justify-center text-white transition-colors">
                  <FaFacebook />
                </a>
                <a href="https://youtube.com/Piroozapp" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-indigo-800 hover:bg-indigo-700 flex items-center justify-center text-white transition-colors">
                  <FaYoutube />
                </a>
              </div>
              <a href="mailto:support@pingquack.com" className="text-indigo-200 hover:text-white transition">support@pingquack.com</a>
            </div>
          </div>
          
          <div className="pt-6 border-t border-indigo-800 text-center text-sm text-indigo-300">
            <p>© 2024 پیروز Pirooz. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{
        __html: `
          .bloom {
            transition: all 0.3s ease;
          }
          .bloom:hover {
            transform: scale(1.1);
            filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.5));
          }
          .card-hover {
            transition: all 0.3s ease;
          }
          .card-hover:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
          }
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
          .floating {
            animation: float 3s ease-in-out infinite;
          }
        `
      }} />
    </div>
  );
};

export default VocabularyGarden; 